import React, { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdminDiscountPage = () => {
  const [products, setProducts] = useState([]);
  const [discountProducts, setDiscountProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const token = localStorage.getItem("adminToken");

  // Fetch all products and discount products
  useEffect(() => {
    fetchAllProducts();
    fetchDiscountProducts();
    fetchCategories();
  }, []);

  const fetchAllProducts = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        "https://afric-fresh-backend.onrender.com/admin/product-subscrition",
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to fetch products.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDiscountProducts = async () => {
    try {
      const response = await axios.get(
        "https://afric-fresh-backend.onrender.com/discount",
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setDiscountProducts(response.data);
    } catch (error) {
      console.error("Error fetching discount products:", error);
      toast.error("Failed to fetch discount products.");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        "https://afric-fresh-backend.onrender.com/admin/subscrition/categories",
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Filter products by category and search term
  const filteredProducts = () => {
    let filtered = products;
    
    if (activeCategory !== "All") {
      filtered = filtered.filter(product => product.category === activeCategory);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.price.includes(searchTerm)
      );
    }
    
    return filtered;
  };

  // Toggle product selection with discount input
  const toggleProductSelection = (product) => {
    // Don't allow selection if product is out of stock
    if (product.quantity <= 0) {
      toast.warning(`"${product.name}" is out of stock and cannot be added to discounts. Please restock first.`);
      return;
    }

    setSelectedProducts(prev => {
      const existingIndex = prev.findIndex(p => p.id === product.id);
      
      if (existingIndex >= 0) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [
          ...prev,
          {
            ...product,
            discount: 0, // Default discount
            originalPrice: product.price // Store original price
          }
        ];
      }
    });
  };

  // Handle discount percentage change
  const handleDiscountChange = (productId, value) => {
    setSelectedProducts(prev =>
      prev.map(product =>
        product.id === productId
          ? {
              ...product,
              discount: Math.min(100, Math.max(0, Number(value))), // Ensure between 0-100
              price: calculateDiscountedPrice(product.originalPrice, value)
            }
          : product
      )
    );
  };

  // Calculate discounted price
  const calculateDiscountedPrice = (originalPrice, discountPercentage) => {
    const price = parseFloat(originalPrice);
    const discount = parseFloat(discountPercentage);
    return (price * (1 - discount / 100)).toFixed(2);
  };

  // Add selected products to discount list
  const addDiscountProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Please select at least one product.");
      return;
    }

    // Check for any out-of-stock products in selection
    const outOfStockProducts = selectedProducts.filter(p => p.quantity <= 0);
    if (outOfStockProducts.length > 0) {
      toast.error(
        `Cannot add ${outOfStockProducts.length} out-of-stock product(s) to discounts.`,
        { autoClose: 5000 }
      );
      return;
    }

    setLoading(true);
    try {
      // Prepare products with originalPrice
      const productsToSend = selectedProducts.map(product => ({
        ...product,
        originalPrice: product.originalPrice || product.price
      }));

      await axios.post(
        "https://afric-fresh-backend.onrender.com/discount/add-product",
        { products: productsToSend },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      toast.success("Products added to discount list successfully!");
      setSelectedProducts([]);
      setShowSelectionModal(false);
      fetchDiscountProducts();
    } catch (error) {
      console.error("Error adding discount products:", error);
      toast.error("Failed to add products to discount list.");
    } finally {
      setLoading(false);
    }
  };

  // Remove product from discount list
  const removeDiscountProduct = async (id) => {
    try {
      await axios.delete(
        `https://afric-fresh-backend.onrender.com/discount/delete-product/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      toast.success("Product removed from discount list.");
      fetchDiscountProducts();
    } catch (error) {
      console.error("Error removing discount product:", error);
      toast.error("Failed to remove product from discount list.");
    }
  };

  return (
    <div className="p-6">
      <ToastContainer />
      <style>{`
        .out-of-stock-overlay {
          position: absolute;
          inset: 0;
          background-color: rgba(239, 68, 68, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }
        .out-of-stock-badge {
          background-color: #ef4444;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }
      `}</style>
      
      <h1 className="text-2xl font-bold mb-4">Admin - Manage Discount Products</h1>

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl">Discount Products</h2>
        <button
          onClick={() => setShowSelectionModal(true)}
          className="bg-blue-500 text-white py-2 px-4 rounded"
        >
          Add Discount Products
        </button>
      </div>

      {/* Discount Products List */}
      {isLoading ? (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-solid"></div>
        </div>
      ) : discountProducts.length === 0 ? (
        <p className="text-center text-gray-500">No discount products available.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {discountProducts.map((product) => (
            <div
              key={product._id}
              className="border p-4 rounded shadow relative bg-white"
            >
              {/* Discount Badge */}
              {product.discount > 0 && (
                <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded">
                  -{product.discount}%
                </div>
              )}
              
              {/* Product Image */}
              <img
                src={product.img}
                alt={product.name}
                className="h-40 w-full object-cover rounded-md"
              />
              
              {/* Product Details */}
              <div className="mt-3">
                <h3 className="font-bold text-sm">{product.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-lg font-bold text-green-600">
                    ${product.price.toLocaleString()}
                  </p> 
                  {product.originalPrice && (
                    <p className="text-sm line-through text-gray-500">
                      ${product.originalPrice.toLocaleString()}
                    </p>
                  )}
                </div>
                <p className="text-sm text-gray-700 mt-1">Brand: {product.brand}</p>
                <p className="text-sm text-gray-700 mt-1">{product.description}</p>
                <p className={`text-sm mt-1 ${
                  product.quantity <= 0 ? 'text-red-500 font-bold' : 'text-gray-700'
                }`}>
                  {product.quantity <= 0 ? 'Out of stock' : `${product.quantity} items left`}
                </p>
              </div>
              
              {/* Remove Button */}
              <button
                onClick={() => removeDiscountProduct(product._id)}
                className="mt-3 w-full bg-red-500 text-white py-1 px-3 rounded text-sm"
              >
                Remove Discount
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Product Selection Modal */}
      {showSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Select Products for Discount</h2>
                <button
                  onClick={() => setShowSelectionModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Filter Controls */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-4 pb-4 border-b">
                <div className="flex flex-wrap gap-2">
                  <button 
                    onClick={() => setActiveCategory('All')}
                    className={`px-3 py-1 rounded-full text-sm ${activeCategory === 'All' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                  >
                    All
                  </button>
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setActiveCategory(category)}
                      className={`px-3 py-1 rounded-full text-sm ${activeCategory === category ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-3 items-center">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border rounded p-2 w-full md:w-48"
                  />
                  
                  <button
                    onClick={addDiscountProducts}
                    disabled={loading || selectedProducts.length === 0}
                    className={`bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2 ${
                      (loading || selectedProducts.length === 0) ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>Add Discount Products ({selectedProducts.length})</>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Products Grid - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4">
              {filteredProducts().length === 0 ? (
                <div className="text-center py-8">
                  <p>No products found.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredProducts().map(product => {
                    const isSelected = selectedProducts.some(p => p.id === product.id);
                    const selectedProduct = isSelected 
                      ? selectedProducts.find(p => p.id === product.id)
                      : null;
                    const isOutOfStock = product.quantity <= 0;

                    return (
                      <div 
                        key={product.id}
                        className={`border rounded-lg overflow-hidden relative ${
                          isSelected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-gray-200'
                        } ${isOutOfStock ? 'opacity-70' : ''}`}
                      >
                        {/* Out of stock overlay */}
                        {isOutOfStock && (
                          <div className="out-of-stock-overlay">
                            <span className="out-of-stock-badge">OUT OF STOCK</span>
                          </div>
                        )}
                        
                        <div className="h-40 overflow-hidden bg-gray-100">
                          {product.img ? (
                            <img src={product.img} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-gray-400">No image</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="p-3">
                          <h3 className="font-semibold truncate">{product.name}</h3>
                          <p className="text-gray-600 text-sm">{product.brand}</p>
                          <p className="text-blue-600 font-medium mt-1">${product.price}</p>
                          <p className="mt-1 text-xs bg-gray-100 inline-block px-2 py-0.5 rounded">
                            {product.category}
                          </p>
                          
                          {/* Stock status */}
                          <p className={`text-xs mt-1 ${
                            isOutOfStock ? 'text-red-500 font-bold' : 'text-gray-500'
                          }`}>
                            {isOutOfStock ? 'Out of stock' : `${product.quantity} available`}
                          </p>

                          {isSelected && !isOutOfStock && (
                            <div className="mt-2">
                              <label className="block text-sm text-gray-700 mb-1">
                                Discount (%):
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={selectedProduct?.discount || 0}
                                onChange={(e) => handleDiscountChange(product.id, e.target.value)}
                                className="w-full p-2 border rounded"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                New price: ${selectedProduct?.price || product.price}
                              </p>
                            </div>
                          )}
                          
                          <button
                            onClick={() => toggleProductSelection(product)}
                            disabled={isOutOfStock}
                            className={`mt-3 w-full py-1.5 rounded ${
                              isSelected ? 'bg-blue-500 text-white' : 
                              isOutOfStock ? 'bg-gray-300 cursor-not-allowed' : 'bg-gray-200 text-gray-800'
                            }`}
                          >
                            {isSelected ? 'Selected' : 
                             isOutOfStock ? 'Out of Stock' : 'Select Product'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDiscountPage;